import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Plus, Search, Filter, Upload, CheckCircle, XCircle, AlertCircle, FileText } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'

interface Entrada {
  id: string
  tipo: 'dizimo' | 'ofertas' | 'missoes' | 'doacoes'
  valor: number
  data: string
  responsavel: {
    tipo: 'visitante' | 'membro' | 'anonimo'
    nome?: string
  }
  observacoes?: string
  metodoPagamento?: 'dinheiro' | 'pix' | 'transferencia' | 'cartao'
  statusConciliacao?: 'pendente' | 'conciliado' | 'divergente'
}

export function EntradasPage() {
  const navigate = useNavigate()

  const [entradas, setEntradas] = useState<Entrada[]>([
    {
      id: '1',
      tipo: 'dizimo',
      valor: 500.00,
      data: '2024-01-15',
      responsavel: { tipo: 'membro', nome: 'João Silva' },
      observacoes: 'Dízimo mensal',
      metodoPagamento: 'pix',
      statusConciliacao: 'conciliado'
    },
    {
      id: '2',
      tipo: 'ofertas',
      valor: 200.00,
      data: '2024-01-15',
      responsavel: { tipo: 'visitante', nome: 'Maria Santos' },
      metodoPagamento: 'dinheiro',
      statusConciliacao: 'pendente'
    },
    {
      id: '3',
      tipo: 'missoes',
      valor: 150.00,
      data: '2024-01-14',
      responsavel: { tipo: 'anonimo' },
      observacoes: 'Oferta para missões',
      metodoPagamento: 'transferencia',
      statusConciliacao: 'divergente'
    },
    {
      id: '4',
      tipo: 'doacoes',
      valor: 300.00,
      data: '2024-01-13',
      responsavel: { tipo: 'membro', nome: 'Pedro Costa' },
      metodoPagamento: 'pix',
      statusConciliacao: 'conciliado'
    }
  ])
  const [filtroTipo, setFiltroTipo] = useState<string>('todos')
  const [filtroData, setFiltroData] = useState<string>('')
  const [filtroResponsavel, setFiltroResponsavel] = useState<string>('todos')
  const [filtroConciliacao, setFiltroConciliacao] = useState<string>('todos')
  const [busca, setBusca] = useState('')
  const [editingValor, setEditingValor] = useState<{id: string, valor: string} | null>(null)

  const entradasFiltradas = entradas.filter(entrada => {
    if (filtroTipo !== 'todos' && entrada.tipo !== filtroTipo) return false
    if (filtroData && entrada.data !== filtroData) return false
    if (filtroResponsavel !== 'todos' && entrada.responsavel.tipo !== filtroResponsavel) return false
    if (busca && !entrada.responsavel.nome?.toLowerCase().includes(busca.toLowerCase())) return false
    if (filtroConciliacao !== 'todos' && entrada.statusConciliacao !== filtroConciliacao) return false
    return true
  })

  const totalEntradas = entradasFiltradas.reduce((sum, entrada) => sum + entrada.valor, 0)

  const estatisticas = {
    totalRegistros: entradasFiltradas.length,
    totalDizimos: entradasFiltradas.filter(e => e.tipo === 'dizimo').reduce((sum, e) => sum + e.valor, 0),
    totalOfertas: entradasFiltradas.filter(e => e.tipo === 'ofertas').reduce((sum, e) => sum + e.valor, 0),
    totalMissoes: entradasFiltradas.filter(e => e.tipo === 'missoes').reduce((sum, e) => sum + e.valor, 0),
    totalDoacoes: entradasFiltradas.filter(e => e.tipo === 'doacoes').reduce((sum, e) => sum + e.valor, 0),
    conciliadas: entradasFiltradas.filter(e => e.statusConciliacao === 'conciliado').length,
    pendentes: entradasFiltradas.filter(e => e.statusConciliacao === 'pendente').length,
    divergentes: entradasFiltradas.filter(e => e.statusConciliacao === 'divergente').length
  }

  const getTipoLabel = (tipo: string) => {
    const labels = {
      dizimo: 'Dízimo',
      ofertas: 'Ofertas',
      missoes: 'Missões',
      doacoes: 'Doações'
    }
    return labels[tipo as keyof typeof labels] || tipo
  }

  const getResponsavelLabel = (responsavel: { tipo: string; nome?: string }) => {
    if (responsavel.tipo === 'anonimo') return 'Anônimo'
    if (responsavel.nome) return responsavel.nome
    return responsavel.tipo === 'membro' ? 'Membro' : 'Visitante'
  }

  const getStatusConciliacaoIcon = (status?: string) => {
    switch (status) {
      case 'conciliado':
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case 'pendente':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'divergente':
        return <XCircle className="h-4 w-4 text-red-500" />
      default:
        return null
    }
  }

  const getStatusConciliacaoText = (status?: string) => {
    switch (status) {
      case 'conciliado':
        return 'Conciliado'
      case 'pendente':
        return 'Pendente'
      case 'divergente':
        return 'Divergente'
      default:
        return '-'
    }
  }

  const getMetodoPagamentoText = (metodo?: string) => {
    switch (metodo) {
      case 'dinheiro':
        return 'Dinheiro'
      case 'pix':
        return 'PIX'
      case 'transferencia':
        return 'Transferência'
      case 'cartao':
        return 'Cartão'
      default:
        return '-'
    }
  }

  const handleStatusConciliacaoClick = (entradaId: string, currentStatus?: string) => {
    const statusOptions = ['pendente', 'conciliado', 'divergente']
    const currentIndex = statusOptions.indexOf(currentStatus || 'pendente')
    const nextIndex = (currentIndex + 1) % statusOptions.length
    const newStatus = statusOptions[nextIndex] as 'pendente' | 'conciliado' | 'divergente'
    
    setEntradas(entradas.map(entrada => 
      entrada.id === entradaId ? { ...entrada, statusConciliacao: newStatus } : entrada
    ))
  }

  const handleValorClick = (entradaId: string, currentValor: number) => {
    // Só permite edição se o status for divergente
    const entrada = entradas.find(e => e.id === entradaId)
    if (entrada?.statusConciliacao === 'divergente') {
      setEditingValor({ id: entradaId, valor: currentValor.toString() })
    }
  }

  const handleValorSave = (entradaId: string) => {
    if (editingValor && editingValor.id === entradaId) {
      const novoValor = parseFloat(editingValor.valor)
      if (!isNaN(novoValor) && novoValor > 0) {
        setEntradas(entradas.map(entrada => 
          entrada.id === entradaId ? { ...entrada, valor: novoValor } : entrada
        ))
      }
      setEditingValor(null)
    }
  }

  const handleValorCancel = () => {
    setEditingValor(null)
  }

  const handleValorKeyPress = (e: React.KeyboardEvent, entradaId: string) => {
    if (e.key === 'Enter') {
      handleValorSave(entradaId)
    } else if (e.key === 'Escape') {
      handleValorCancel()
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="mr-4"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
          <div>
            <h1 className="text-2xl font-bold">Entradas Financeiras</h1>
            <p className="text-muted-foreground">Gerencie dízimos, ofertas e doações</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/financas/administracao/entradas/conciliacao-bancaria')}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Conciliação Bancária
          </Button>
          <Button onClick={() => navigate('/financas/administracao/entradas/cadastrar')} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Nova Entrada
          </Button>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Filtrado</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalEntradas)}</div>
            <p className="text-xs text-muted-foreground">
              {estatisticas.totalRegistros} registros
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Dízimos</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(estatisticas.totalDizimos)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ofertas</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(estatisticas.totalOfertas)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conciliadas</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{estatisticas.conciliadas}</div>
            <p className="text-xs text-muted-foreground">
              {estatisticas.pendentes} pendentes, {estatisticas.divergentes} divergentes
            </p>
          </CardContent>
        </Card>
      </div>



      {/* Filtros e Tabela Unificados */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Entradas ({entradasFiltradas.length})
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Seção de Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 p-4 bg-muted/20 rounded-lg">
            <div className="space-y-2">
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Nome do responsável..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="dizimo">Dízimo</SelectItem>
                  <SelectItem value="ofertas">Ofertas</SelectItem>
                  <SelectItem value="missoes">Missões</SelectItem>
                  <SelectItem value="doacoes">Doações</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Data</Label>
              <Input
                type="date"
                value={filtroData}
                onChange={(e) => setFiltroData(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Responsável</Label>
              <Select value={filtroResponsavel} onValueChange={setFiltroResponsavel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="membro">Membro</SelectItem>
                  <SelectItem value="visitante">Visitante</SelectItem>
                  <SelectItem value="anonimo">Anônimo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Conciliação</Label>
              <Select value={filtroConciliacao} onValueChange={setFiltroConciliacao}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="conciliado">Conciliado</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="divergente">Divergente</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Tabela */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Data</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Tipo</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Valor</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Responsável</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Método</th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                    Conciliação
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-muted-foreground">Observações</th>
                </tr>
              </thead>
              <tbody>
                {entradasFiltradas.map((entrada) => (
                  <tr key={entrada.id} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-4">
                      {new Date(entrada.data).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant="outline">
                        {getTipoLabel(entrada.tipo)}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 font-medium">
                      {editingValor?.id === entrada.id ? (
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={editingValor.valor}
                            onChange={(e) => setEditingValor({ ...editingValor, valor: e.target.value })}
                            onKeyDown={(e) => handleValorKeyPress(e, entrada.id)}
                            onBlur={() => handleValorSave(entrada.id)}
                            className="w-24 h-8 text-sm"
                            autoFocus
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleValorSave(entrada.id)}
                            className="h-6 w-6 p-0"
                          >
                            <CheckCircle className="h-3 w-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleValorCancel}
                            className="h-6 w-6 p-0"
                          >
                            <XCircle className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div 
                          className={`${entrada.statusConciliacao === 'divergente' ? 'cursor-pointer hover:bg-muted/50 rounded px-2 py-1 transition-colors' : ''}`}
                          onClick={() => handleValorClick(entrada.id, entrada.valor)}
                          title={entrada.statusConciliacao === 'divergente' ? 'Clique para editar o valor' : ''}
                        >
                          {formatCurrency(entrada.valor)}
                          {entrada.statusConciliacao === 'divergente' && (
                            <span className="text-xs text-muted-foreground ml-1">(clique para editar)</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {getResponsavelLabel(entrada.responsavel)}
                    </td>
                    <td className="py-3 px-4">
                      {getMetodoPagamentoText(entrada.metodoPagamento)}
                    </td>
                    <td className="py-3 px-4">
                      <div 
                        className="flex items-center gap-2 cursor-pointer rounded px-2 py-1 relative group"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleStatusConciliacaoClick(entrada.id, entrada.statusConciliacao);
                        }}
                        title="Clique para alterar o status"
                      >
                        {getStatusConciliacaoIcon(entrada.statusConciliacao)}
                        <span className="text-sm">
                          {getStatusConciliacaoText(entrada.statusConciliacao)}
                        </span>
                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                          Clique para alterar o status
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {entrada.observacoes || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  )
}